import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

// @ts-nocheck
/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Organisations
 */ import { useMutation, useQuery } from "@tanstack/react-query";

import type {
  CreateOrganisationRequest,
  ErrorResponse,
  ListOrganisationsParams,
  ListOrganisationsResponse,
  OrganisationEmailDomainListResponse,
  OrganisationResponse,
  UpdateOrganisationRequest,
  ValidationErrorResponse,
} from "../models";

import { restFetch } from "../../../../shell/api/auth.ts";
import { unauthenticatedRestFetch } from "../../../../shell/api/auth.ts";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export type createOrganisationResponse201 = {
  data: OrganisationResponse;
  status: 201;
};

export type createOrganisationResponse400 = {
  data: ErrorResponse;
  status: 400;
};

export type createOrganisationResponse401 = {
  data: ErrorResponse;
  status: 401;
};

export type createOrganisationResponse422 = {
  data: ValidationErrorResponse;
  status: 422;
};

export type createOrganisationResponse500 = {
  data: ErrorResponse;
  status: 500;
};

export type createOrganisationResponseSuccess =
  createOrganisationResponse201 & {
    headers: Headers;
  };
export type createOrganisationResponseError = (
  | createOrganisationResponse400
  | createOrganisationResponse401
  | createOrganisationResponse422
  | createOrganisationResponse500
) & {
  headers: Headers;
};

export type createOrganisationResponse =
  | createOrganisationResponseSuccess
  | createOrganisationResponseError;

export const getCreateOrganisationUrl = () => {
  return `/api/v1/organisations`;
};

/**
 * @summary Create organisation
 */
export const createOrganisation = async (
  createOrganisationRequest: CreateOrganisationRequest,
  options?: RequestInit,
): Promise<createOrganisationResponse> => {
  return restFetch<createOrganisationResponse>(getCreateOrganisationUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(createOrganisationRequest),
  });
};

export const getCreateOrganisationMutationOptions = <
  TError = ErrorResponse | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOrganisation>>,
    TError,
    { data: CreateOrganisationRequest },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOrganisation>>,
  TError,
  { data: CreateOrganisationRequest },
  TContext
> => {
  const mutationKey = ["createOrganisation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOrganisation>>,
    { data: CreateOrganisationRequest }
  > = props => {
    const { data } = props ?? {};

    return createOrganisation(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOrganisation>>
>;
export type CreateOrganisationMutationBody = CreateOrganisationRequest;
export type CreateOrganisationMutationError =
  | ErrorResponse
  | ValidationErrorResponse;

/**
 * @summary Create organisation
 */
export const useCreateOrganisation = <
  TError = ErrorResponse | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createOrganisation>>,
      TError,
      { data: CreateOrganisationRequest },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createOrganisation>>,
  TError,
  { data: CreateOrganisationRequest },
  TContext
> => {
  return useMutation(
    getCreateOrganisationMutationOptions(options),
    queryClient,
  );
};
export type listOrganisationsResponse200 = {
  data: ListOrganisationsResponse;
  status: 200;
};

export type listOrganisationsResponse401 = {
  data: ErrorResponse;
  status: 401;
};

export type listOrganisationsResponse500 = {
  data: ErrorResponse;
  status: 500;
};

export type listOrganisationsResponseSuccess = listOrganisationsResponse200 & {
  headers: Headers;
};
export type listOrganisationsResponseError = (
  | listOrganisationsResponse401
  | listOrganisationsResponse500
) & {
  headers: Headers;
};

export type listOrganisationsResponse =
  | listOrganisationsResponseSuccess
  | listOrganisationsResponseError;

export const getListOrganisationsUrl = (params?: ListOrganisationsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/organisations?${stringifiedParams}`
    : `/api/v1/organisations`;
};

/**
 * @summary List organisations
 */
export const listOrganisations = async (
  params?: ListOrganisationsParams,
  options?: RequestInit,
): Promise<listOrganisationsResponse> => {
  return restFetch<listOrganisationsResponse>(getListOrganisationsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getListOrganisationsQueryKey = (
  params?: ListOrganisationsParams,
) => {
  return [`/api/v1/organisations`, ...(params ? [params] : [])] as const;
};

export const getListOrganisationsQueryOptions = <
  TData = Awaited<ReturnType<typeof listOrganisations>>,
  TError = ErrorResponse,
>(
  params?: ListOrganisationsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganisations>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOrganisationsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listOrganisations>>
  > = ({ signal }) => listOrganisations(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listOrganisations>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListOrganisationsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOrganisations>>
>;
export type ListOrganisationsQueryError = ErrorResponse;

export function useListOrganisations<
  TData = Awaited<ReturnType<typeof listOrganisations>>,
  TError = ErrorResponse,
>(
  params: undefined | ListOrganisationsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganisations>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganisations>>,
          TError,
          Awaited<ReturnType<typeof listOrganisations>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganisations<
  TData = Awaited<ReturnType<typeof listOrganisations>>,
  TError = ErrorResponse,
>(
  params?: ListOrganisationsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganisations>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganisations>>,
          TError,
          Awaited<ReturnType<typeof listOrganisations>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListOrganisations<
  TData = Awaited<ReturnType<typeof listOrganisations>>,
  TError = ErrorResponse,
>(
  params?: ListOrganisationsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganisations>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List organisations
 */

export function useListOrganisations<
  TData = Awaited<ReturnType<typeof listOrganisations>>,
  TError = ErrorResponse,
>(
  params?: ListOrganisationsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listOrganisations>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListOrganisationsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getOrganisationResponse200 = {
  data: OrganisationResponse;
  status: 200;
};

export type getOrganisationResponse401 = {
  data: ErrorResponse;
  status: 401;
};

export type getOrganisationResponse404 = {
  data: ErrorResponse;
  status: 404;
};

export type getOrganisationResponse500 = {
  data: ErrorResponse;
  status: 500;
};

export type getOrganisationResponseSuccess = getOrganisationResponse200 & {
  headers: Headers;
};
export type getOrganisationResponseError = (
  | getOrganisationResponse401
  | getOrganisationResponse404
  | getOrganisationResponse500
) & {
  headers: Headers;
};

export type getOrganisationResponse =
  | getOrganisationResponseSuccess
  | getOrganisationResponseError;

export const getGetOrganisationUrl = (organisationId: string) => {
  return `/api/v1/organisations/${organisationId}`;
};

/**
 * @summary Get organisation
 */
export const getOrganisation = async (
  organisationId: string,
  options?: RequestInit,
): Promise<getOrganisationResponse> => {
  return restFetch<getOrganisationResponse>(
    getGetOrganisationUrl(organisationId),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetOrganisationQueryKey = (organisationId: string) => {
  return [`/api/v1/organisations/${organisationId}`] as const;
};

export const getGetOrganisationQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = ErrorResponse,
>(
  organisationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOrganisationQueryKey(organisationId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganisation>>> = ({
    signal,
  }) => getOrganisation(organisationId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: organisationId !== null && organisationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisation>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetOrganisationQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganisation>>
>;
export type GetOrganisationQueryError = ErrorResponse;

export function useGetOrganisation<
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = ErrorResponse,
>(
  organisationId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisation>>,
          TError,
          Awaited<ReturnType<typeof getOrganisation>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOrganisation<
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = ErrorResponse,
>(
  organisationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisation>>,
          TError,
          Awaited<ReturnType<typeof getOrganisation>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOrganisation<
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = ErrorResponse,
>(
  organisationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get organisation
 */

export function useGetOrganisation<
  TData = Awaited<ReturnType<typeof getOrganisation>>,
  TError = ErrorResponse,
>(
  organisationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisation>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetOrganisationQueryOptions(organisationId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type updateOrganisationResponse200 = {
  data: OrganisationResponse;
  status: 200;
};

export type updateOrganisationResponse400 = {
  data: ErrorResponse;
  status: 400;
};

export type updateOrganisationResponse401 = {
  data: ErrorResponse;
  status: 401;
};

export type updateOrganisationResponse404 = {
  data: ErrorResponse;
  status: 404;
};

export type updateOrganisationResponse422 = {
  data: ValidationErrorResponse;
  status: 422;
};

export type updateOrganisationResponse500 = {
  data: ErrorResponse;
  status: 500;
};

export type updateOrganisationResponseSuccess =
  updateOrganisationResponse200 & {
    headers: Headers;
  };
export type updateOrganisationResponseError = (
  | updateOrganisationResponse400
  | updateOrganisationResponse401
  | updateOrganisationResponse404
  | updateOrganisationResponse422
  | updateOrganisationResponse500
) & {
  headers: Headers;
};

export type updateOrganisationResponse =
  | updateOrganisationResponseSuccess
  | updateOrganisationResponseError;

export const getUpdateOrganisationUrl = (organisationId: string) => {
  return `/api/v1/organisations/${organisationId}`;
};

/**
 * @summary Update organisation
 */
export const updateOrganisation = async (
  organisationId: string,
  updateOrganisationRequest: UpdateOrganisationRequest,
  options?: RequestInit,
): Promise<updateOrganisationResponse> => {
  return restFetch<updateOrganisationResponse>(
    getUpdateOrganisationUrl(organisationId),
    {
      ...options,
      method: "PUT",
      headers: { "Content-Type": "application/json", ...options?.headers },
      body: JSON.stringify(updateOrganisationRequest),
    },
  );
};

export const getUpdateOrganisationMutationOptions = <
  TError = ErrorResponse | ValidationErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOrganisation>>,
    TError,
    { organisationId: string; data: UpdateOrganisationRequest },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOrganisation>>,
  TError,
  { organisationId: string; data: UpdateOrganisationRequest },
  TContext
> => {
  const mutationKey = ["updateOrganisation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOrganisation>>,
    { organisationId: string; data: UpdateOrganisationRequest }
  > = props => {
    const { organisationId, data } = props ?? {};

    return updateOrganisation(organisationId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOrganisation>>
>;
export type UpdateOrganisationMutationBody = UpdateOrganisationRequest;
export type UpdateOrganisationMutationError =
  | ErrorResponse
  | ValidationErrorResponse;

/**
 * @summary Update organisation
 */
export const useUpdateOrganisation = <
  TError = ErrorResponse | ValidationErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateOrganisation>>,
      TError,
      { organisationId: string; data: UpdateOrganisationRequest },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateOrganisation>>,
  TError,
  { organisationId: string; data: UpdateOrganisationRequest },
  TContext
> => {
  return useMutation(
    getUpdateOrganisationMutationOptions(options),
    queryClient,
  );
};
export type deleteOrganisationResponse204 = {
  data: void;
  status: 204;
};

export type deleteOrganisationResponse401 = {
  data: ErrorResponse;
  status: 401;
};

export type deleteOrganisationResponse404 = {
  data: ErrorResponse;
  status: 404;
};

export type deleteOrganisationResponse500 = {
  data: ErrorResponse;
  status: 500;
};

export type deleteOrganisationResponseSuccess =
  deleteOrganisationResponse204 & {
    headers: Headers;
  };
export type deleteOrganisationResponseError = (
  | deleteOrganisationResponse401
  | deleteOrganisationResponse404
  | deleteOrganisationResponse500
) & {
  headers: Headers;
};

export type deleteOrganisationResponse =
  | deleteOrganisationResponseSuccess
  | deleteOrganisationResponseError;

export const getDeleteOrganisationUrl = (organisationId: string) => {
  return `/api/v1/organisations/${organisationId}`;
};

/**
 * @summary Delete organisation
 */
export const deleteOrganisation = async (
  organisationId: string,
  options?: RequestInit,
): Promise<deleteOrganisationResponse> => {
  return restFetch<deleteOrganisationResponse>(
    getDeleteOrganisationUrl(organisationId),
    {
      ...options,
      method: "DELETE",
    },
  );
};

export const getDeleteOrganisationMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOrganisation>>,
    TError,
    { organisationId: string },
    TContext
  >;
  request?: SecondParameter<typeof restFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteOrganisation>>,
  TError,
  { organisationId: string },
  TContext
> => {
  const mutationKey = ["deleteOrganisation"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteOrganisation>>,
    { organisationId: string }
  > = props => {
    const { organisationId } = props ?? {};

    return deleteOrganisation(organisationId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOrganisationMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteOrganisation>>
>;

export type DeleteOrganisationMutationError = ErrorResponse;

/**
 * @summary Delete organisation
 */
export const useDeleteOrganisation = <
  TError = ErrorResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteOrganisation>>,
      TError,
      { organisationId: string },
      TContext
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteOrganisation>>,
  TError,
  { organisationId: string },
  TContext
> => {
  return useMutation(
    getDeleteOrganisationMutationOptions(options),
    queryClient,
  );
};
export type getOrganisationBySlugResponse200 = {
  data: OrganisationResponse;
  status: 200;
};

export type getOrganisationBySlugResponse401 = {
  data: ErrorResponse;
  status: 401;
};

export type getOrganisationBySlugResponse404 = {
  data: ErrorResponse;
  status: 404;
};

export type getOrganisationBySlugResponse500 = {
  data: ErrorResponse;
  status: 500;
};

export type getOrganisationBySlugResponseSuccess =
  getOrganisationBySlugResponse200 & {
    headers: Headers;
  };
export type getOrganisationBySlugResponseError = (
  | getOrganisationBySlugResponse401
  | getOrganisationBySlugResponse404
  | getOrganisationBySlugResponse500
) & {
  headers: Headers;
};

export type getOrganisationBySlugResponse =
  | getOrganisationBySlugResponseSuccess
  | getOrganisationBySlugResponseError;

export const getGetOrganisationBySlugUrl = (slug: string) => {
  return `/api/v1/organisations/slug/${slug}`;
};

/**
 * @summary Get organisation by slug
 */
export const getOrganisationBySlug = async (
  slug: string,
  options?: RequestInit,
): Promise<getOrganisationBySlugResponse> => {
  return restFetch<getOrganisationBySlugResponse>(
    getGetOrganisationBySlugUrl(slug),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetOrganisationBySlugQueryKey = (slug: string) => {
  return [`/api/v1/organisations/slug/${slug}`] as const;
};

export const getGetOrganisationBySlugQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganisationBySlug>>,
  TError = ErrorResponse,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationBySlug>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOrganisationBySlugQueryKey(slug);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOrganisationBySlug>>
  > = ({ signal }) =>
    getOrganisationBySlug(slug, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: slug !== null && slug !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationBySlug>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetOrganisationBySlugQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganisationBySlug>>
>;
export type GetOrganisationBySlugQueryError = ErrorResponse;

export function useGetOrganisationBySlug<
  TData = Awaited<ReturnType<typeof getOrganisationBySlug>>,
  TError = ErrorResponse,
>(
  slug: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationBySlug>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationBySlug>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationBySlug>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOrganisationBySlug<
  TData = Awaited<ReturnType<typeof getOrganisationBySlug>>,
  TError = ErrorResponse,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationBySlug>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationBySlug>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationBySlug>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOrganisationBySlug<
  TData = Awaited<ReturnType<typeof getOrganisationBySlug>>,
  TError = ErrorResponse,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationBySlug>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get organisation by slug
 */

export function useGetOrganisationBySlug<
  TData = Awaited<ReturnType<typeof getOrganisationBySlug>>,
  TError = ErrorResponse,
>(
  slug: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationBySlug>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof restFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetOrganisationBySlugQueryOptions(slug, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

export type getOrganisationByEmailDomainResponse200 = {
  data: OrganisationEmailDomainListResponse;
  status: 200;
};

export type getOrganisationByEmailDomainResponse401 = {
  data: ErrorResponse;
  status: 401;
};

export type getOrganisationByEmailDomainResponse404 = {
  data: ErrorResponse;
  status: 404;
};

export type getOrganisationByEmailDomainResponse500 = {
  data: ErrorResponse;
  status: 500;
};

export type getOrganisationByEmailDomainResponseSuccess =
  getOrganisationByEmailDomainResponse200 & {
    headers: Headers;
  };
export type getOrganisationByEmailDomainResponseError = (
  | getOrganisationByEmailDomainResponse401
  | getOrganisationByEmailDomainResponse404
  | getOrganisationByEmailDomainResponse500
) & {
  headers: Headers;
};

export type getOrganisationByEmailDomainResponse =
  | getOrganisationByEmailDomainResponseSuccess
  | getOrganisationByEmailDomainResponseError;

export const getGetOrganisationByEmailDomainUrl = (emailDomain: string) => {
  return `/api/v1/organisations/email-domain/${emailDomain}`;
};

/**
 * @summary Lookup organisation by email domain
 */
export const getOrganisationByEmailDomain = async (
  emailDomain: string,
  options?: RequestInit,
): Promise<getOrganisationByEmailDomainResponse> => {
  return unauthenticatedRestFetch<getOrganisationByEmailDomainResponse>(
    getGetOrganisationByEmailDomainUrl(emailDomain),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetOrganisationByEmailDomainQueryKey = (
  emailDomain: string,
) => {
  return [`/api/v1/organisations/email-domain/${emailDomain}`] as const;
};

export const getGetOrganisationByEmailDomainQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
  TError = ErrorResponse,
>(
  emailDomain: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof unauthenticatedRestFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetOrganisationByEmailDomainQueryKey(emailDomain);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOrganisationByEmailDomain>>
  > = ({ signal }) =>
    getOrganisationByEmailDomain(emailDomain, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: emailDomain !== null && emailDomain !== undefined,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetOrganisationByEmailDomainQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganisationByEmailDomain>>
>;
export type GetOrganisationByEmailDomainQueryError = ErrorResponse;

export function useGetOrganisationByEmailDomain<
  TData = Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
  TError = ErrorResponse,
>(
  emailDomain: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationByEmailDomain>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof unauthenticatedRestFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOrganisationByEmailDomain<
  TData = Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
  TError = ErrorResponse,
>(
  emailDomain: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationByEmailDomain>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof unauthenticatedRestFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetOrganisationByEmailDomain<
  TData = Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
  TError = ErrorResponse,
>(
  emailDomain: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof unauthenticatedRestFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Lookup organisation by email domain
 */

export function useGetOrganisationByEmailDomain<
  TData = Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
  TError = ErrorResponse,
>(
  emailDomain: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getOrganisationByEmailDomain>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof unauthenticatedRestFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetOrganisationByEmailDomainQueryOptions(
    emailDomain,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
